// src/components/DataAuditPage.js

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlayCircle, faSpinner, faCheckCircle, faExclamationTriangle,
  faFileMedicalAlt, faTableList, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { functions } from '../firebase'; // Ensure 'functions' is exported from your firebase setup
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext'; // Your Auth context

// JSDoc comments for type hinting (optional, for developer understanding)
/**
 * @typedef {object} IssueDetail
 * @property {string} collectionName
 * @property {string} documentId
 * @property {string} [problematicOrgId]
 * @property {string} [referencedField]
 * @property {string} [referencedValue]
 * @property {string} reason
 */

/**
 * @typedef {object} DataIntegrityReport
 * @property {boolean} success
 * @property {string} message
 * @property {IssueDetail[]} issues
 * @property {Object<string, number>} issuesByCollection
 * @property {number} totalIssues
 */

// Define the callable function reference
const generateReportCallable = httpsCallable(functions, 'generateDataIntegrityReport');
const AUTHORIZED_EMAIL = "jonstewart24@gmail.com"; // Centralize for clarity

function DataAuditPage() {
  const { currentUser, loadingAuth } = useAuth();

  const [reportResults, setReportResults] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [userMessage, setUserMessage] = useState({ type: '', text: '' });

  const handleRunReport = async () => {
    setIsLoadingReport(true);
    setError(null);
    setUserMessage({ type: '', text: '' });
    setReportResults(null);

    try {
      // --- Enhanced Authentication Check and Logging ---
      console.log("handleRunReport: Current User from context:", currentUser);
      if (!currentUser) {
        console.error("handleRunReport: No currentUser found. Aborting.");
        setError("No active user session. Please log in again.");
        setUserMessage({ type: 'danger', text: "Authentication error: No user session." });
        setIsLoadingReport(false);
        return;
      }

      console.log("handleRunReport: Current User UID:", currentUser.uid);
      console.log("handleRunReport: Current User Email:", currentUser.email);

      // Client-side authorization check (primary guard is server-side)
      if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
        console.warn(`handleRunReport: User ${currentUser.email} is not authorized.`);
        const authError = new Error("You are not authorized to perform this action.");
        // authError.code = "permission-denied"; // Can add for more specific client-side handling if needed
        throw authError;
      }

      try {
        console.log("handleRunReport: Attempting to refresh ID token...");
        await currentUser.getIdToken(true); // Force refresh ID token
        console.log("handleRunReport: ID token refreshed successfully.");
      } catch (tokenError) {
        console.error("handleRunReport: Error refreshing ID token:", tokenError);
        setError("Failed to refresh authentication token. Please try logging out and back in or check your network connection.");
        setUserMessage({ type: 'danger', text: "Authentication token refresh failed." });
        setIsLoadingReport(false);
        return;
      }
      // --- End of Enhanced Authentication Check ---

      console.log("handleRunReport: Proceeding to call generateReportCallable...");
      const result = await generateReportCallable();
      const data = result.data;

      if (data && data.success) {
        console.log("handleRunReport: Report generated successfully by function.", data);
        setReportResults(data);
        setUserMessage({ type: 'success', text: data.message || 'Report generated successfully.' });
      } else {
        console.error("handleRunReport: Function reported failure or unexpected data structure.", data);
        throw new Error(data?.message || 'Failed to generate report. The function reported an issue or returned unexpected data.');
      }
    } catch (err) {
      console.error("handleRunReport: Error during report generation or function call:", err);
      let displayError = "An unknown error occurred while generating the report.";

      if (err.code && err.message) { // Firebase HttpsError
        displayError = `Error (${err.code}): ${err.message}`;
      } else if (err.message) { // Generic JavaScript Error
        displayError = err.message;
      }
      setError(displayError);
      setUserMessage({ type: 'danger', text: `Failed to generate report. ${displayError}` });
    } finally {
      setIsLoadingReport(false);
    }
  };

  // --- Render Logic ---

  if (loadingAuth) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verifying access...</span>
        </div>
        <p className="mt-2">Verifying access...</p>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
    return (
      <div className="container mt-3">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
            <li className="breadcrumb-item">Admin</li>
            <li className="breadcrumb-item active" aria-current="page">Access Denied</li>
          </ol>
        </nav>
        <div className="alert alert-danger" role="alert">
          <h4><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Access Denied</h4>
          <p>You do not have the necessary permissions to view this page or perform this action.</p>
        </div>
        <Link to="/dashboard" className="btn btn-secondary">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
          <li className="breadcrumb-item"><Link to="/admin">Admin</Link></li>
          <li className="breadcrumb-item active" aria-current="page">Data Integrity Report</li>
        </ol>
      </nav>
      <h2 className="mb-4 text-start">
        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />Data Integrity Report
      </h2>

      {userMessage.text && (
        <div className={`alert alert-${userMessage.type || 'info'} alert-dismissible fade show mx-1 mb-3`} role="alert">
          {userMessage.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="me-2" />}
          {(userMessage.type === 'danger' || userMessage.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />}
          {userMessage.text}
          <button type="button" className="btn-close" onClick={() => setUserMessage({ type: '', text: '' })} aria-label="Close"></button>
        </div>
      )}
      {error && !userMessage.text && (
        <div className="alert alert-danger mx-1 mb-3" role="alert">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> {error}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body text-center">
          <p className="card-text">Click the button below to scan the database and authentication system for potential data integrity issues.</p>
          <button
            type="button"
            className="btn btn-primary btn-lg px-4"
            onClick={handleRunReport}
            disabled={isLoadingReport}
          >
            {isLoadingReport ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Generating Report...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlayCircle} className="me-2" />
                Run Data Integrity Report
              </>
            )}
          </button>
          {isLoadingReport && <p className="mt-2 text-muted small">This process may take several minutes depending on data size.</p>}
        </div>
      </div>

      {isLoadingReport && !reportResults && (
          <div className="text-center mt-4 card shadow-sm">
              <div className="card-body">
                  <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-2" />
                  <p className="mb-0">Fetching and analyzing data, please wait...</p>
              </div>
          </div>
      )}

      {reportResults && !isLoadingReport && (
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0"><FontAwesomeIcon icon={faTableList} className="me-2" />Report Results</h5>
          </div>
          <div className="card-body">
            <div className="alert alert-info small">
                <strong>Overall Status:</strong> {reportResults.message} <br />
                <strong>Total Issues Found:</strong> <span className={`badge fs-6 bg-${reportResults.totalIssues > 0 ? 'danger' : 'success'}`}>{reportResults.totalIssues}</span>
            </div>

            {reportResults.totalIssues > 0 && (
              <>
                <h6 className="mt-4">Issues by Category:</h6>
                <div className="list-group list-group-flush mb-3">
                  {Object.entries(reportResults.issuesByCollection)
                    .filter(([, count]) => typeof count === 'number' && count > 0)
                    .map(([collectionName, count]) => (
                      <div key={collectionName} className="list-group-item d-flex justify-content-between align-items-center ps-0">
                        {collectionName.replace(/([A-Z])/g, ' $1').trim()}
                        <span className={`badge bg-warning text-dark rounded-pill`}>{count}</span>
                      </div>
                  ))}
                </div>

                <h6 className="mt-4">Detailed Issues:</h6>
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-sm align-middle small">
                    <thead className="table-light">
                      <tr>
                        <th scope="col" style={{width: '3%'}}>#</th>
                        <th scope="col" style={{width: '15%'}}>Category/Collection</th>
                        <th scope="col" style={{width: '20%'}}>Document/User ID</th>
                        <th scope="col">Reason</th>
                        <th scope="col" style={{width: '10%'}}>OrgId</th>
                        <th scope="col" style={{width: '10%'}}>Ref. Field</th>
                        <th scope="col" style={{width: '10%'}}>Ref. Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(reportResults.issues) && reportResults.issues.map((issue, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{issue.collectionName}</td>
                          <td style={{ wordBreak: 'break-all' }}>{issue.documentId}</td>
                          <td style={{ whiteSpace: 'pre-wrap'}}>{issue.reason}</td>
                          <td>{issue.problematicOrgId || '-'}</td>
                          <td>{issue.referencedField || '-'}</td>
                          <td style={{ wordBreak: 'break-all' }}>{issue.referencedValue || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
             {reportResults.totalIssues === 0 && (
                <div className="alert alert-success text-center">
                    <FontAwesomeIcon icon={faCheckCircle} size="2x" className="mb-2" /><br/>
                    No data integrity issues found. Everything looks good!
                </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataAuditPage;